import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  message,
  Alert,
  Input,
  Tag,
  Popconfirm,
  Transfer,
  Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  User,
  getUserAuthorizations,
  batchAuthorizeDevices,
  batchRevokeAuthorizations,
  updateAuthorizationStatus,
  searchDevicesForAuthorization,
  DeviceInfo,
} from '../../services/user';
import apiError from '../../utils/apiError';

const { Search } = Input;
const { TabPane } = Tabs;

interface UserAuthorizationModalProps {
  visible: boolean;
  user: User;
  onClose: () => void;
}

const UserAuthorizationModal: React.FC<UserAuthorizationModalProps> = ({
  visible,
  user,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);

  // 已授权设备
  const [authorizedDevices, setAuthorizedDevices] = useState<DeviceInfo[]>([]);
  const [selectedAuthorizedKeys, setSelectedAuthorizedKeys] = useState<React.Key[]>([]);

  const [errorMsg, setErrorMsg] = useState('');

  // 可授权设备（用于批量授权）
  const [availableDevices, setAvailableDevices] = useState<DeviceInfo[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);

  // 加载已授权设备
  const loadAuthorizedDevices = async () => {
    setLoading(true);
    try {
      const res = await getUserAuthorizations(user.id, 1); // 只加载启用的授权
      const data = res.data || res;
      setAuthorizedDevices(data.devices || []);
    } catch (error: any) {
      setErrorMsg(error.message || '加载授权设备失败');
      apiError.clear();
    } finally {
      setLoading(false);
    }
  };

  // 搜索可授权设备
  const searchDevices = async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await searchDevicesForAuthorization(user.id, keyword, 1, 100);
      const data = res.data || res;
      setAvailableDevices(data.devices || []);
    } catch (error: any) {
      setErrorMsg(error.message || '搜索设备失败');
      apiError.clear();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setErrorMsg('');
      loadAuthorizedDevices();
      searchDevices();
    }
  }, [visible]);

  // 批量授权
  const handleBatchAuthorize = async () => {
    if (selectedDeviceIds.length === 0) {
      message.warning('请选择要授权的设备');
      return;
    }

    try {
      await batchAuthorizeDevices(user.id, selectedDeviceIds);
      message.success(`成功授权 ${selectedDeviceIds.length} 个设备`);
      setSelectedDeviceIds([]);
      loadAuthorizedDevices();
      setActiveTab('1'); // 切换到已授权标签页
    } catch (error: any) {
      setErrorMsg(error.message || '批量授权失败');
      apiError.clear();
    }
  };

  // 批量撤销授权
  const handleBatchRevoke = async () => {
    if (selectedAuthorizedKeys.length === 0) {
      message.warning('请选择要撤销授权的设备');
      return;
    }

    const deviceIds = authorizedDevices
      .filter((d) => selectedAuthorizedKeys.includes(d.deviceId))
      .map((d) => d.deviceId);

    try {
      await batchRevokeAuthorizations(user.id, deviceIds);
      message.success(`成功撤销 ${deviceIds.length} 个设备的授权`);
      setSelectedAuthorizedKeys([]);
      loadAuthorizedDevices();
    } catch (error: any) {
      setErrorMsg(error.message || '批量撤销授权失败');
      apiError.clear();
    }
  };

  // 撤销单个授权
  const handleRevokeOne = async (deviceId: string) => {
    try {
      await batchRevokeAuthorizations(user.id, [deviceId]);
      message.success('撤销授权成功');
      loadAuthorizedDevices();
    } catch (error: any) {
      setErrorMsg(error.message || '撤销授权失败');
      apiError.clear();
    }
  };

  // 已授权设备表格列
  const authorizedColumns: ColumnsType<DeviceInfo> = [
    {
      title: '设备ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 150,
    },
    {
      title: '设备名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'default'}>
          {status === 1 ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="确定要撤销该设备的授权吗？"
          onConfirm={() => handleRevokeOne(record.deviceId)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            撤销
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // 可授权设备表格列
  const availableColumns: ColumnsType<DeviceInfo> = [
    {
      title: '设备ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 150,
    },
    {
      title: '设备名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'default'}>
          {status === 1 ? '正常' : '禁用'}
        </Tag>
      ),
    },
  ];

  return (
    <Modal
      title={`设备授权 - ${user.name || user.phone}`}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <div style={{ minHeight: 42, marginBottom: 16 }}>
        {errorMsg && (
          <Alert message={errorMsg} type="error" showIcon closable onClose={() => setErrorMsg('')} />
        )}
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={`已授权设备 (${authorizedDevices.length})`} key="1">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchRevoke}
                disabled={selectedAuthorizedKeys.length === 0}
              >
                批量撤销 ({selectedAuthorizedKeys.length})
              </Button>
            </Space>

            <Table
              columns={authorizedColumns}
              dataSource={authorizedDevices}
              rowKey="deviceId"
              loading={loading}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedAuthorizedKeys,
                onChange: setSelectedAuthorizedKeys,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 个设备`,
              }}
            />
          </Space>
        </TabPane>

        <TabPane tab="批量授权" key="2">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Search
              placeholder="搜索设备ID或位置"
              allowClear
              onSearch={(value) => {
                setSearchKeyword(value);
                searchDevices(value);
              }}
              style={{ width: '100%' }}
            />

            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleBatchAuthorize}
                disabled={selectedDeviceIds.length === 0}
              >
                批量授权 ({selectedDeviceIds.length})
              </Button>
              <span style={{ color: '#999' }}>
                在下方表格中勾选要授权的设备，然后点击"批量授权"按钮
              </span>
            </Space>

            <Table
              columns={availableColumns}
              dataSource={availableDevices}
              rowKey="deviceId"
              loading={loading}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedDeviceIds,
                onChange: (keys) => setSelectedDeviceIds(keys as string[]),
                getCheckboxProps: (record) => ({
                  // 已授权的设备不可选
                  disabled: authorizedDevices.some((d) => d.deviceId === record.deviceId),
                }),
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 个设备`,
              }}
            />
          </Space>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default UserAuthorizationModal;
